/**
 * Agrupamento e override da matriz de Eisenhower (spec 4.1 e 9.2).
 *
 * O quadrante NAO e recalculado aqui. A API ja devolve `task.quadrant` como o
 * valor efetivo — derivado no servidor quando `quadrantAuto`, ou o fixado pelo
 * usuario quando nao. Derivar de novo no cliente usaria outro `agora` e faria
 * cards trocarem de coluna entre o carregamento e o primeiro render, sem que
 * nada tivesse acontecido.
 */

import { useMemo } from 'react';
import { DONE_WINDOW_DAYS, QUADRANT, type Quadrant, type Task } from '@cleartask/shared';
import { useTaskList, useUpdateTask } from '../tasks/useTasks.js';

/** Ordem de leitura da grade 2x2 (spec 9.2): I e II na linha de cima. */
export const QUADRANT_ORDER: readonly Quadrant[] = [
  QUADRANT.URGENT_IMPORTANT,
  QUADRANT.NOT_URGENT_IMPORTANT,
  QUADRANT.URGENT_NOT_IMPORTANT,
  QUADRANT.NEITHER,
] as const;

export type TasksByQuadrant = Record<Quadrant, Task[]>;

function emptyGroups(): TasksByQuadrant {
  return { 1: [], 2: [], 3: [], 4: [] };
}

/**
 * Tarefas da matriz, separadas por quadrante E por status.
 *
 * A consulta pede `includeDone` para que cada quadrante possa mostrar o que ja
 * foi feito num painel recolhivel. As duas colecoes vem SEPARADAS de proposito,
 * e nao como uma lista unica que a tela filtra: o contador do cabecalho conta
 * `byQuadrant`, e uma lista misturada faria o numero incluir trabalho concluido
 * — a matriz passaria a cobrar o que ja saiu. E a mesma decisao da §8g na aba
 * Tarefas.
 */
export function useMatrixTasks() {
  // Sem as subtarefas, pela mesma razao de "Todas": a matriz e tela de TRIAGEM,
  // e quem decide o que fazer agora decide sobre o trabalho inteiro. A filha ao
  // lado do pai, nos dois no mesmo quadrante, faria a grade contar duas vezes a
  // mesma decisao.
  // O recorte das concluidas vale aqui pelo mesmo motivo da aba Tarefas, e com
  // mais forca: sem ele a gaveta traria tudo que ja foi concluido naquele
  // quadrante, para sempre — multiplicado por quatro quadrantes na MESMA
  // resposta.
  const query = useTaskList({
    view: 'matrix',
    includeDone: true,
    parentId: 'null',
    doneDays: DONE_WINDOW_DAYS,
  });

  const { byQuadrant, doneByQuadrant } = useMemo(() => {
    const abertas = emptyGroups();
    const concluidas = emptyGroups();

    for (const task of query.data?.tasks ?? []) {
      // Sem quadrante gravado, a tarefa cai em Q4 em vez de sumir. Um card
      // invisivel na matriz e pior que um card no quadrante errado: ninguem
      // procura o que nao sabe que existe.
      const grupo = task.status === 'done' ? concluidas : abertas;
      grupo[task.quadrant ?? QUADRANT.NEITHER].push(task);
    }

    // Mais recente primeiro, como o grupo "executados" da aba Tarefas: num
    // registro do que foi feito, o que interessa e o que acabou de sair.
    for (const quadrant of QUADRANT_ORDER) {
      concluidas[quadrant].sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0));
    }

    return { byQuadrant: abertas, doneByQuadrant: concluidas };
  }, [query.data?.tasks]);

  return {
    byQuadrant,
    doneByQuadrant,
    now: query.data?.now ?? Date.now(),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Fixa o quadrante — o efeito de arrastar um card entre colunas (spec 4.1).
 *
 * Os dois campos andam juntos: gravar `quadrant` sem desligar `quadrantAuto`
 * faria o card voltar sozinho ao quadrante antigo na leitura seguinte, e o
 * arrastar pareceria simplesmente nao ter funcionado.
 */
export function usePinQuadrant() {
  const updateTask = useUpdateTask();

  return {
    isPending: updateTask.isPending,
    pin: (task: Task, quadrant: Quadrant) =>
      updateTask.mutate({ id: task.id, patch: { quadrant, quadrantAuto: false } }),
  };
}

/**
 * Devolve a tarefa a classificacao automatica (spec 4.1: acao "voltar ao
 * automatico" no menu de contexto do card).
 *
 * Manda apenas `quadrantAuto: true` e deixa o servidor recalcular. Enviar um
 * quadrante junto seria aplicar a regra do lado errado da fronteira.
 */
export function useResetQuadrant() {
  const updateTask = useUpdateTask();

  return {
    isPending: updateTask.isPending,
    reset: (task: Task) => updateTask.mutate({ id: task.id, patch: { quadrantAuto: true } }),
  };
}
